import React, { useEffect, useState } from 'react';
import ClaimRewardModal from './claim-reward-modal';
import { useTranslation } from 'react-i18next';
import {
    handleClaimClick,
    closeClaimModal,
    claimAll,
    updateCountdown,
    getStatusText,
    getMissionName,
    getRewardText,
    fetchDailyMissions,
    checkMemberTotalExpiringPoint,
    getMissionType,
} from './dailyMissionUtil';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { useHistory } from 'react-router-dom';

const ITEMS_PER_PAGE = 10;

const DailyMissionsPage = () => {
    const [missions, setMissions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [expiringDate, setExpiringDate] = useState('');
    const [expiringPoints, setExpiringPoints] = useState(0);
    const [timeLeft, setTimeLeft] = useState('');
    const [memberPointsBalance, setMemberPointsBalance] = useState(0);
    const [isPointBalanceLoad, setIsPointBalanceLoad] = useState(false);
    const [isClaimModalOpen, setIsClaimModalOpen] = useState(false);
    const [rewardPoints, setRewardPoints] = useState(0);
    const [selectedMission, setSelectedMission] = useState(null);
    const [isLuckyWheelEnabled, setIsLuckyWheelEnabled] = useState(false);
    const [lastUpdated, setLastUpdated] = useState('');

    const history = useHistory();
    const { t } = useTranslation(['dailyMission']);

    useEffect(() => {
        let isMounted = true;

        const checkIsDailyMissionEnabled = async () => {
            try {
                const res = await window.SPL_Member.getDailyMissionCampaignV2();

                if (!res?.isDailyMissionEnabled) {
                    history.push('/');
                    return false;
                }

                if (res?.luckyWheelDetail?.luckyWheelSettingId) {
                    setIsLuckyWheelEnabled(true);
                }

                return true;
            } catch (error) {
                console.error('Failed to fetch Daily Mission Campaign:', error);
                history.push('/');
                return false;
            }
        };

        const fetchData = async () => {
            const isEnabled = await checkIsDailyMissionEnabled();
            if (!isEnabled) return;
            fetchDailyMissions(setMissions, setIsLoading);
            updateCountdown(setTimeLeft);
            getMemberMissionPoints();

            await checkMemberTotalExpiringPoint(
                (points) => {
                    if (isMounted) setExpiringPoints(points);
                },
                (date) => {
                    if (isMounted) setExpiringDate(date);
                }
            );
        };

        fetchData();
        setLastUpdated(new Date().toLocaleString('en-GB'));

        const countdownInterval = setInterval(() => {
            if (isMounted) updateCountdown(setTimeLeft);
        }, 1000);

        return () => {
            isMounted = false;
            clearInterval(countdownInterval);
        };
    }, []);

    const totalPages = Math.ceil(missions.length / ITEMS_PER_PAGE);
    const currentMissions = missions.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
    const startItem = (currentPage - 1) * ITEMS_PER_PAGE + 1;
    const endItem = Math.min(currentPage * ITEMS_PER_PAGE, missions.length);

    const handlePageChange = (pageNumber) => {
        if (pageNumber >= 1 && pageNumber <= totalPages) {
            setCurrentPage(pageNumber);
        }
    };

    const onClaimClick = (missionId, points, missionObj) => {
        setRewardPoints(points);
        setSelectedMission(missionObj);
        setIsClaimModalOpen(true);

        handleClaimClick(missionId, points, setRewardPoints, setIsLoading, () => fetchDailyMissions(setMissions, setIsLoading), getMemberMissionPoints, setIsClaimModalOpen);
    };

    const onClaimAll = () => claimAll(setIsLoading, () => fetchDailyMissions(setMissions, setIsLoading), getMemberMissionPoints);

    const onCloseClaimModal = () => {
        setIsClaimModalOpen(false);
        setSelectedMission(null);
    };

    const getMemberMissionPoints = () => {
        setIsPointBalanceLoad(true);
        window.SPL_Member.getMemberMissionPoints()
            .then((memberPoint) => {
                if (memberPoint || memberPoint === 0) {
                    setMemberPointsBalance(memberPoint);
                }
                setIsPointBalanceLoad(false);
            })
            .catch(() => {
                setIsPointBalanceLoad(false);
            });
    };

    if (isLoading) {
        return <div>Loading missions...</div>;
    }

    return (
        <div className='missions-page'>
            <div className='top-section'>
                <div className='time-btn-info'>
                    <div className='left-info'>
                        <div>
                            <span>{t('dailyMission.dailyMission')}</span>
                            <span className='time-info'>00:00 - 23:59 GMT+8</span>
                        </div>
                        <div className='countdown-info'>
                            <span>{t('dailyMission.countdownTime')}</span>
                            <span className='time-info'>{timeLeft}</span>
                        </div>
                    </div>
                    <div className='right-buttons'>
                        {isLuckyWheelEnabled && (
                            <button className='spin-button' onClick={() => (window.location.href = '/lucky-wheel-landing')}>
                                {t('dailyMission.spinToWin')}
                            </button>
                        )}
                        <button className='claim-all-button' onClick={onClaimAll}>
                            {isLoading ? 'Processing...' : t('dailyMission.claimAll')}
                        </button>
                    </div>
                </div>
                <div className='points-info'>
                    <div className='points-container'>
                        <strong>{t('dailyMission.totalPoint')}:</strong>
                        <strong className='balance-points'>{memberPointsBalance || 0}</strong>
                        <LazyLoadImage className='refresh-icon' src='/public/html/images/daily-mission/refresh.svg' onClick={getMemberMissionPoints} />
                    </div>
                    <div className='date-container'>
                        <span className='last-updated-text'>
                            {t('dailyMission.lastUpdated')}: {lastUpdated}
                        </span>
                        <span className='expiry'>{t('dailyMission.expiry', { expiringPoints, expiringDate })}</span>
                    </div>
                </div>
            </div>

            <div className='missions-container'>
                {missions.length === 0 ? (
                    <div className='no-missions'>
                        <p>No missions available</p>
                    </div>
                ) : (
                    <table className='missions-table'>
                        <thead>
                            <tr>
                                <th>{t('dailyMission.completion')}</th>
                                <th>{t('dailyMission.missionName')}</th>
                                <th>{t('dailyMission.target')}</th>
                                <th>{t('dailyMission.missionDate')}</th>
                                <th>{t('dailyMission.missionProgress')}</th>
                                <th>{t('dailyMission.reward')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentMissions.map((mission, index) => (
                                <tr key={index} className={`status-${mission.status}`}>
                                    <td>
                                        <button
                                            disabled={mission.status === 'pending' || isLoading}
                                            className={`${mission.status}-btn`}
                                            onClick={mission.status === 'A' ? () => onClaimClick(mission.id, mission.reward, mission) : undefined}
                                        >
                                            {t(getStatusText(mission.status))}
                                        </button>
                                    </td>
                                    <td>{getMissionName(t, mission.missionName)}</td>
                                    <td>{getMissionType(t, mission.targetType, mission.target, window.localStorage.currencyLang)}</td>
                                    <td>{mission.missionStartDate ? new Date(mission.missionStartDate).toLocaleDateString() : '-'}</td>
                                    <td>
                                        {mission.isCompleted ? (
                                            <>
                                                <span className='progress-completed'>{t('dailyMission.completed')}</span>
                                                <div className='progress-bar'>
                                                    <div className='progress-fill' style={{ width: `100%` }}></div>
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                {mission.targetType === 'TIME_RANGE' ? (
                                                    <span>{mission.target}</span>
                                                ) : (
                                                    <>
                                                        <span>
                                                            {mission.progress || 0}/{mission.target}
                                                        </span>
                                                        <div className='progress-bar'>
                                                            <div
                                                                className='progress-fill'
                                                                style={{
                                                                    width: `${mission.target ? (mission.progress / mission.target) * 100 : 0}%`,
                                                                }}
                                                            ></div>
                                                        </div>
                                                    </>
                                                )}
                                            </>
                                        )}
                                    </td>
                                    <td>{getRewardText(t, mission.rewardType, mission.reward, window.localStorage.currencyLang)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {isClaimModalOpen && selectedMission && (
                <ClaimRewardModal rewardPoints={selectedMission.reward} rewardType={selectedMission.rewardType} onClose={onCloseClaimModal} />
            )}

            {currentMissions.length > 0 && (
                <div className='pagination-controls'>
                    <div className='pagination-summary'>{t('dailyMission.showResult', { start: startItem, end: endItem, total: missions.length })}</div>
                    <div className='pagination-buttons'>
                        <button className='page-button' onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>
                            {t('dailyMission.previous')}
                        </button>
                        {Array.from({ length: totalPages }, (_, i) => (
                            <button key={i + 1} className={`page-button ${currentPage === i + 1 ? 'active' : ''}`} onClick={() => handlePageChange(i + 1)}>
                                {i + 1}
                            </button>
                        ))}
                        <button className='page-button' onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>
                            {t('dailyMission.next')}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DailyMissionsPage;
